import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import styled from 'styled-components';

import LandingPageHome from '../components/LandingPageHome';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import { DefaultLocale } from '../providers/tolgee';
import APILogger from '../services/APILogger';
import CoreAPIService from '../services/CoreAPIService';
import { generateUniqueId } from '../utils/general';
import {
  detectAndBestEffortSetLanguage,
  getLocaleRedirectIfNeeded,
} from '../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';

const Home: NextPageWithLayout = () => {
  useRoutePrefetch('/[tenantId]/home');
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  return (
    <Container>
      <LandingPageLayout
        title="Curipod - Writing Practice Students Love"
        subTitle="Students write, get real-time AI feedback, discuss and rewrite, all in one class period."
        locale={DefaultLocale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  const logger = new APILogger();
  logger.setRequestId(
    (context.req.headers['x-cloud-trace-context'] as string) ||
      generateUniqueId().toString(),
  );
  try {
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;

    if (session) {
      const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(
        session.user.uid,
        logger,
      );

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/home`,
        },
      };
    }

    // Non-authenticated: redirect non-default locale users to their localized landing.
    // Must come after the session check to avoid a redirect loop with /lang/[locale]/
    // which redirects authenticated users back to /.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    const queryClient = new QueryClient();

    return {
      props: {
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  } catch (error) {
    logger.error('INDEX_PAGE_GSSP_ERROR', {
      error: error instanceof Error ? error.message : JSON.stringify(error),
    });

    // On session check failure, still redirect non-default locale users.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  }
};

export default Home;
